import React from "react";
import { useState, useEffect, Fragment, useContext } from "react";
import axios from "axios";
import { API_URL } from "api/config";
import { toast } from "react-toastify";
import moment from "moment";
import { Menu } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/solid";
import { Formik, Form, Field, ErrorMessage } from "formik";
import DatePicker from "react-datepicker";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import { Transition, Dialog } from "@headlessui/react";
import * as Yup from "yup";
import { AuthContext } from "context/userContext";



const MyBookings = () => {
  const [tabActiveState, setTabActiveState] = useState("Upcoming");
  const [activeBookingCat, setActiveBookingCat] = useState("upcoming");
  const [bookings, setBookings] = useState([]);
  const [bookingCounts, setBookingCounts] = useState<any>({});
  const [editingBooking, setEditingBooking] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const authContext = useContext(AuthContext);
  const { ownerData } = authContext;

  const bookingCategory = [
    "Upcoming",
    "Active",
    "Today Checking-in",
    "Today Checking-out",
    "History",
  ];
  const token = localStorage.getItem("token");

  const handleDeleteClick = (booking: any) => {
    setIsDeleteModalOpen(true);
    setBookingId(booking._id);
  };
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        if(!token) {
          return toast.info(`Please login to view bookings`)
        }
        const response = await axios.get(`${API_URL}/owner/owner-bookings`, {
          headers: {
            token: token,
          },
        });
        if (response.data.error === false) {
          setBookings(response.data.bookings);
        } else {
          toast.error(`${response.data.message}`);
        }
      } catch (error) {
        toast.error(`Failed to fetch your bookings`);
        console.error("Error fetching bookings", error);
      }
    };
    fetchBookings();
  }, []);

  useEffect(() => {
    calculateBookingCounts();
  }, [bookings]);


  // Function to count bookings in each category
  const calculateBookingCounts = () => {
    const counts: any = {
      Upcoming: 0,
      "Today Checking-in": 0,
      "Today Checking-out": 0,
      Active: 0,
      History: 0,
    };

    bookings.forEach((booking: any) => {
      const checkinDate = moment(booking.checkinDate).format("YYYY-MM-DD");
      const checkoutDate = moment(booking.checkoutDate).format("YYYY-MM-DD");

      if (checkinDate === today) counts["Today Checking-in"]++;
      if (checkoutDate === today) counts["Today Checking-out"]++;
      if (today >= checkinDate && today <= checkoutDate) counts["Active"]++;
      if (checkinDate > today) counts["Upcoming"]++;
      if (checkoutDate < today) counts["History"]++;
    });
    setBookingCounts(counts);
  };

  const today = moment().format("YYYY-MM-DD");
  // Filter bookings based on active category
  const filteredBookings = bookings.filter((booking: any) => {
    const checkinDate = moment(booking.checkinDate).format("YYYY-MM-DD");
    const checkoutDate = moment(booking.checkoutDate).format("YYYY-MM-DD");

    switch (tabActiveState) {
      case "Upcoming":
        return checkinDate > today;
      case "Active":
        return today >= checkinDate && today <= checkoutDate;
      case "Today Checking-in":
        return checkinDate === today;
      case "Today Checking-out":
        return checkoutDate === today;

      case "History":
        return checkoutDate < today;
      default:
        return true;
    }
  });

  const handleEditBooking = (booking: any) => {
    setEditingBooking(booking);
    setIsEditing(true);
  };

  // Define Yup validation schema
  const bookingSchema = Yup.object().shape({
    guestName: Yup.string().required("Guest name is required"),
    guestPhone: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
      .required("Guest phone is required"),
    guestEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
    numOfGuest: Yup.number()
      .min(1, "At least 1 guest is required")
      .required("Guest count is required"),
    checkinDate: Yup.date().required("Check-in date is required"),
    checkoutDate: Yup.date()
      .required("Check-out date is required")
      .min(Yup.ref("checkinDate"), "Checkout must be after check-in"),
  });

  const formatDate = (date: string | Date | null) => {
    if (!date) return "";
    const localDate = new Date(date);
    // Adjust to local timezone and extract YYYY-MM-DD format
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(localDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formattedStartDate = formatDate(editingBooking?.checkinDate);
  const formattedEndDate = formatDate(editingBooking?.checkoutDate);

  const confirmDeleteBooking = async () => {
    setIsDeleteModalOpen(false);
    try {
      const response = await axios.post(
        `${API_URL}/owner/delete-booking`,
        { bookingId: bookingId },
        {
          headers: {
            token: token,
          },
        }
      );
      if (response.data.error === false) {
        toast.success(`${response.data.message}`);
        setBookings((prevBookings) =>
          prevBookings.filter((booking: any) => booking._id !== bookingId)
        );
      }
    } catch (error) {
      console.log("Erron in deleting booking", error);
      toast.error("Failed to delete booking");
    }
  };

  // Build booked date intervals for the current property's other bookings
  const getBookedIntervals = (currentBooking: any) => {
    try {
      const currentPropId = currentBooking?.propertyId?._id || currentBooking?.propertyId;
      return bookings
        .filter((b: any) => {
          const propId = b?.propertyId?._id || b?.propertyId;
          return (
            b?._id !== currentBooking?._id &&
            propId &&
            currentPropId &&
            String(propId) === String(currentPropId)
          );
        })
        .map((b: any) => ({
          start: new Date(b.checkinDate),
          end: new Date(b.checkoutDate),
        }));
    } catch (e) {
      return [] as { start: Date; end: Date }[];
    }
  };

  const isDateInsideIntervals = (
    date: Date,
    intervals: { start: Date; end: Date }[]
  ) => {
    return intervals.some(({ start, end }) => date >= start && date <= end);
  };
    // Get check-in and check-out dates for highlighting
  const getCheckinCheckoutDates = (currentBooking: any) => {
    try {
      const currentPropId = currentBooking?.propertyId?._id || currentBooking?.propertyId;
      const dates = bookings
        .filter((b: any) => {
          const propId = b?.propertyId?._id || b?.propertyId;
          return (
            b?._id !== currentBooking?._id &&
            propId &&
            currentPropId &&
            String(propId) === String(currentPropId)
          );
        })
        .flatMap((b: any) => [
          new Date(b.checkinDate),
          new Date(b.checkoutDate)
        ]);
      return dates;
    } catch (e) {
      return [] as Date[];
    }
  };
  const ischeckinDate = (date: Date, intervals: { start: Date; end: Date }[]) => {
    const checkinDate = intervals.map(interval => interval.start);
    const checkoutDate = intervals.map(interval => interval.end);
    if (checkinDate.some(startDate => date.toDateString() === startDate.toDateString()) && checkoutDate.some(endDate => date.toDateString() === endDate.toDateString())) {
      return "both";
    } else if (checkinDate.some(startDate => date.toDateString() === startDate.toDateString())) {
      return "checkin";
    } else if (checkoutDate.some(endDate => date.toDateString() === endDate.toDateString())) {
      return "checkout";
    }
  }
  const isCheckinCheckoutDate = (
    date: Date,
    checkinCheckoutDates: Date[]
  ) => {
    return checkinCheckoutDates.some((checkDate) => 
      date.toDateString() === checkDate.toDateString()
    );
  };

  return (
    <div className="container mt-10">
      <div className="container flex items-center w-full justify-center">
        <div className="top-0 z-10 bg-white flex w-full overflow-x-auto text-sm md:text-base  items-start text-center justify-center">
          {bookingCategory.map((item, index) => (
            <div
              key={index}
              className="cursor-pointer py-4 px-3 flex flex-col items-center text-center"
              onClick={() => {
                if (activeBookingCat !== item) {
                  setActiveBookingCat(item);
                  setTabActiveState(item);
                }
              }}
            >
              <button
                className={`border rounded-full px-4 py-1 ${
                  tabActiveState === item
                    ? "border-2 border-custom-color2 text-black"
                    : "border-black text-neutral-700"
                }`}
              >
                {item} ({bookingCounts[item] || 0})
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Display Filtered Bookings */}
      <div className="w-full flex justify-center">
      <div className="mt-6 flex gap-3 flex-wrap w-full justify-start max-w-[1000px]">
        {filteredBookings.length > 0 ? (
          filteredBookings.map((booking: any) => (
            <div
              key={booking._id}
              className="relative border pl-4 py-4 pr-1 rounded-lg mb-4 w-80"
            >
              <div className="flex justify-between items-center mb-4">
                <p className="lg:text-base text-sm text-black font-medium capitalize truncate" title={booking.guestName}>
                {`${booking.guestName}`}
              </p>
                <div className="relative flex items-center space-x-[-10px]">
                  {booking.propertyId?.cover_image ? (
                    <img
                      src={booking.propertyId?.cover_image}
                      alt=""
                      className="w-9 h-9 border rounded-full"
                    />
                  ) : (
                    <div className="bg-custom-color text-white w-9 h-9 rounded-full border relative">
                      <span className="wil-avatar__name capitalize absolute left-[10px] top-1">{`${booking?.guestName[0]}`}</span>
                    </div>
                  )}
                  {/* Count Badge */}
                  {booking.numOfGuest > 1 ? (
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-200 text-black text-sm font-semibold border-2 border-white rounded-full">
                      {/* User icon (could also use an SVG) */}
                      <span className="relative">
                      <svg
                        className="w-8 h-8 text-gray-600"
                        fill="white"
                        viewBox="0 0 20 20"
                        >
                        <path
                          fillRule="evenodd"
                          d="M10 10a4 4 0 100-8 4 4 0 000 8zm-6 8a6 6 0 1112 0H4z"
                          clipRule="evenodd"
                          />
                      </svg>
                          </span>
                          <span className="absolute justify-center">{`+${booking.numOfGuest - 1}`}</span>
                    </div>
                  ) : (
                    ""
                  )}
                  {/* Three-Dots Menu */}
                  {tabActiveState !== "History" && 
                  <Menu as="div" className="relative !ml-2">
                    <Menu.Button className="p-1 rounded-full hover:bg-gray-200">
                      <EllipsisVerticalIcon className="h-6 w-6 text-black" />
                    </Menu.Button>

                    {/* Dropdown Menu */}
                    <Menu.Items className="absolute right-0 mt-2 w-36 bg-white border rounded-md shadow-lg z-10">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => handleEditBooking(booking)}
                            className={`w-full px-4 py-2 text-sm text-left ${
                              active ? "bg-gray-100" : ""
                            }`}
                          >
                            Edit Booking
                          </button>
                        )}
                      </Menu.Item>
                      {tabActiveState !== "Today Checking-out" && 
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => handleDeleteClick(booking)}
                            className={`w-full px-4 py-2 text-sm text-left ${
                              active ? "bg-gray-100" : ""
                            }`}
                          >
                            Cancel Booking
                          </button>
                        )}
                      </Menu.Item>}
                    </Menu.Items>
                  </Menu>
}
                </div>
              </div>

              {/* <p className="lg:text-base text-sm text-black font-medium capitalize truncate" title={booking.guestName}>
                {`${booking.guestName}`}
              </p> */}
              <p className="text-sm text-neutral-700 mt-4 truncate" title={booking.propertyId?.title}>
                {booking.propertyId?.title}
              </p>
              <p className="lg:text-md text-sm text-[#022a60] mt-2 font-medium capitalize truncate" title={`${moment(booking.checkinDate).format("MMM D, YYYY")} to ${moment(booking.checkoutDate).format("MMM D, YYYY")}`}>
                {`${moment(booking.checkinDate).format("MMM D")} - ${moment(booking.checkoutDate).format("MMM D, YYYY")}`}
              </p>
            </div>
          ))
        ) : (
          <p className="text-center w-full mt-4 text-gray-500">
            No bookings found
          </p>
        )}
      </div>
      </div>
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-max">
          <div className="bg-white p-6 rounded-lg w-96 overflow-auto">
            <h2 className="text-xl font-semibold">Edit Booking</h2>

            <Formik
              initialValues={{
                guestName: editingBooking?.guestName || "",
                guestPhone: editingBooking?.guestPhone || "",
                guestEmail: editingBooking?.guestEmail || "",
                numOfGuest: editingBooking?.numOfGuest || 1,
                checkinDate: formattedStartDate,
                checkoutDate: formattedEndDate,
              }}
              validationSchema={bookingSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const response = await axios.post(
                    `${API_URL}/owner/edit-booking`,
                    {
                      ...values,
                      booking_id: editingBooking._id,
                      propId: editingBooking.propertyId,
                    },
                    { headers: { token } }
                  );

                  if (response.data.error === false) {
                    toast.success("Booking updated successfully!");
                    setBookings((prevBookings: any) =>
                      prevBookings.map((booking: any) =>
                        booking._id === editingBooking._id
                          ? { ...booking, ...values }
                          : booking
                      )
                    );
                    setIsEditing(false);
                  } else {
                    toast.error(response.data.message);
                    setIsEditing(false);
                  }
                } catch (error) {
                  toast.error("Failed to update booking.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form className="space-y-4" id="edit-booking-form">
                  <div id="edit-booking-portal" />
                  {/* Guest Name */}
                  <Field
                    type="text"
                    name="guestName"
                    className="border p-2 w-full bg-white my-3"
                    placeholder="Guest Name"
                  />
                  <ErrorMessage
                    name="guestName"
                    component="div"
                    className="text-red-500 text-sm"
                  />

                  {/* Guest Phone */}
                  <Field
                    type="text"
                    name="guestPhone"
                    className="border p-2 w-full bg-white my-3"
                    placeholder="Guest Phone"
                  />
                  <ErrorMessage
                    name="guestPhone"
                    component="div"
                    className="text-red-500 text-sm"
                  />

                  {/* Guest Email */}
                  <Field
                    type="email"
                    name="guestEmail"
                    className="border p-2 w-full bg-white my-3"
                    placeholder="Guest email"
                  />
                  <ErrorMessage
                    name="guestEmail"
                    component="div"
                    className="text-red-500 text-sm"
                  />

                  {/* No. of Guests */}
                  <Field
                    type="number"
                    name="numOfGuest"
                    className="border p-2 w-full bg-white my-3"
                    placeholder="Number of Guest"
                  />
                  <ErrorMessage
                    name="numOfGuest"
                    component="div"
                    className="text-red-500 text-sm"
                  />

                  {/* Check-in / Check-out Inputs with popup calendars */}
                  <div className="my-3 flex flex-col gap-3 w-full">
                    <DatePicker
                      selected={values?.checkinDate ? new Date(values.checkinDate) : null}
                      onChange={(date: Date | null) => {
                        setFieldValue("checkinDate", date ? formatDate(date) : "");
                        // If checkout is before new checkin, clear it
                        if (date && values?.checkoutDate) {
                          const co = new Date(values.checkoutDate);
                          if (co < date) setFieldValue("checkoutDate", "");
                        }
                      }}
                      selectsStart
                      startDate={values?.checkinDate ? new Date(values.checkinDate) : null}
                      endDate={values?.checkoutDate ? new Date(values.checkoutDate) : null}
                      minDate={new Date()}
                      placeholderText="Check-in date"
                      className="border p-2 w-full bg-white"
                      calendarClassName="edit-booking-calendar"
                      popperPlacement="bottom-start"
                      portalId="edit-booking-portal"
                      showPopperArrow={false}
                      monthsShown={1}
                      renderCustomHeader={(p) => (
                        <DatePickerCustomHeaderTwoMonth {...p} />
                      )}
                      popperClassName="edit-booking-popper"
                      highlightDates={[]}
                      filterDate={(date: Date) => {
                        const intervals = getBookedIntervals(editingBooking);
                        const checkinCheckoutDates = getCheckinCheckoutDates(editingBooking);
                        const isInsideInterval = isDateInsideIntervals(date, intervals);
                        const isCheckinCheckout = isCheckinCheckoutDate(date, checkinCheckoutDates);
                        
                        // Allow check-in and check-out dates to be selectable
                        // Only filter out dates that are inside intervals but not check-in/check-out dates
                        return !(isInsideInterval && !isCheckinCheckout);
                      }}
                      dayClassName={(date: Date) => {
                        const intervals = getBookedIntervals(editingBooking);
                        // const checkinCheckoutDates = getCheckinCheckoutDates(editingBooking);
                        const isInsideInterval = isDateInsideIntervals(date, intervals);
                        const dateType = ischeckinDate(date, intervals);
                        // const isCheckinCheckout = isCheckinCheckoutDate(date, checkinCheckoutDates);
                        if (dateType === "both") {
                          return "booked-date";
                        }else if (dateType === "checkin") {
                          return "checkin-date-bookings";
                        }else if (dateType === "checkout") {
                          return "checkout-date-bookings";
                        }else if (isInsideInterval) {
                          return "booked-date";
                        }
                        return "";
                      }}
                    />

                    <DatePicker
                      selected={values?.checkoutDate ? new Date(values.checkoutDate) : null}
                      onChange={(date: Date | null) => {
                        setFieldValue("checkoutDate", date ? formatDate(date) : "");
                      }}
                      selectsEnd
                      startDate={values?.checkinDate ? new Date(values.checkinDate) : null}
                      endDate={values?.checkoutDate ? new Date(values.checkoutDate) : null}
                      minDate={values?.checkinDate ? new Date(values.checkinDate) : new Date()}
                      placeholderText="Check-out date"
                      className="border p-2 w-full bg-white"
                      calendarClassName="edit-booking-calendar"
                      popperPlacement="bottom-start"
                      portalId="edit-booking-portal"
                      showPopperArrow={false}
                      monthsShown={1}
                      renderCustomHeader={(p) => (
                        <DatePickerCustomHeaderTwoMonth {...p} />
                      )}
                      popperClassName="edit-booking-popper"
                      highlightDates={[]}
                      filterDate={(date: Date) => {
                        const intervals = getBookedIntervals(editingBooking);
                        const checkinCheckoutDates = getCheckinCheckoutDates(editingBooking);
                        const isInsideInterval = isDateInsideIntervals(date, intervals);
                        const isCheckinCheckout = isCheckinCheckoutDate(date, checkinCheckoutDates);
                        
                        // Allow check-in and check-out dates to be selectable
                        // Only filter out dates that are inside intervals but not check-in/check-out dates
                        return !(isInsideInterval && !isCheckinCheckout);
                      }}
                      dayClassName={(date: Date) => {
                        const intervals = getBookedIntervals(editingBooking);
                        const checkinCheckoutDates = getCheckinCheckoutDates(editingBooking);
                        const isInsideInterval = isDateInsideIntervals(date, intervals);
                        const isCheckinCheckout = isCheckinCheckoutDate(date, checkinCheckoutDates);
                        
                        if (isCheckinCheckout) {
                          return "checkin-checkout-date";
                        } else if (isInsideInterval) {
                          return "booked-date";
                        }
                        return "";
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="checkinDate"
                    component="div"
                    className="text-red-500 text-sm"
                  />
                  <ErrorMessage
                    name="checkoutDate"
                    component="div"
                    className="text-red-500 text-sm"
                  />

                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 mr-2 bg-gray-300 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-custom-color text-white rounded"
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
      {/* DELETE ACCOUNT MODAL */}
      <Transition show={isDeleteModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <Dialog.Panel className="bg-white dark:bg-gray-900 p-6 rounded-lg max-w-lg w-full shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-center">
              Confirm Booking Deletion
            </Dialog.Title>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2">
              Are you sure you want to delete your booking? This action cannot
              be undone.
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded-md"
              >
                No, Cancel
              </button>
              <button
                onClick={confirmDeleteBooking}
                className="px-4 py-2 bg-red-600 text-white rounded-md"
              >
                Yes, Delete Booking
              </button>
            </div>
          </Dialog.Panel>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MyBookings;
